/**
 * Console Script: Auto-prefill Checkout Address from Location Data
 * 
 * Usage: Copy and paste this entire script into your browser console on your Shopify storefront
 * It will automatically bind to checkout buttons and prefill the address when clicked
 * 
 * Auto-detection: If location data exists in session storage or cart attributes, it will be used automatically
 */

/**
 * Get location data from session storage
 * @returns {Object|null} Location data object or null if not found
 */
function getLocationData() {
  try {
    const sessionData = sessionStorage.getItem('casa_selected_location_data');
    // if(false) console.log('🔍 Session storage check:', {
    //   hasData: !!sessionData,
    //   rawValue: sessionData
    // });
    
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      // if(false) console.log('✅ Found location data in session storage:', parsed);
      return parsed;
    }
    // if(false) console.log('⚠️ No location data in session storage');
    return null;
  } catch (error) {
    console.error('❌ Error reading location data from session storage:', error);
    return null;
  }
}

/**
 * Get location data from cart attributes
 * @param {Object} cart - Cart object from Shopify
 * @returns {Object|null} Location data object or null if not found
 */
async function getLocationDataFromCart() {
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    if(false) console.log('🛒 Cart check:', {
      hasAttributes: !!(cart.attributes),
      hasLocationData: !!(cart.attributes && cart.attributes.casa_selected_location_data),
      attributes: cart.attributes
    });
    
    if (cart.attributes && cart.attributes.casa_selected_location_data) {
      const locationData = JSON.parse(cart.attributes.casa_selected_location_data);
      if(false) console.log('✅ Found location data in cart attributes:', locationData);
      return locationData;
    }
    if(false) console.log('⚠️ No location data in cart attributes');
    return null;
  } catch (error) {
    console.error('❌ Error reading location data from cart attributes:', error);
    return null;
  }
}

/**
 * Get customer info from window (provided by Liquid)
 * @returns {Object|null} Customer data or null
 */
function getCustomerFromWindow() {
  try {
    if(false) console.log('🔍 getCustomerFromWindow called');
    if(false) console.log('  window.CASA_PREFILL_CUSTOMER:', window.CASA_PREFILL_CUSTOMER);
    if(false) console.log('  window.customer:', window.customer);
    
    const candidate = window.CASA_PREFILL_CUSTOMER || window.customer || null;
    if(false) console.log('  candidate:', candidate);
    
    if (!candidate || typeof candidate !== 'object') {
      if(false) console.log('  ⚠️ No candidate found or not an object');
      return null;
    }

    const firstName = typeof candidate.first_name === 'string' ? candidate.first_name.trim() : 
                      (typeof candidate.firstName === 'string' ? candidate.firstName.trim() : '');
    const lastName = typeof candidate.last_name === 'string' ? candidate.last_name.trim() : 
                     (typeof candidate.lastName === 'string' ? candidate.lastName.trim() : '');
    const phone = typeof candidate.phone === 'string' ? candidate.phone.trim() : '';
    const company = typeof candidate.company === 'string' ? candidate.company.trim() : '';

    const result = { firstName, lastName, phone, company };
    if(false) console.log('  ✅ Extracted customer data:', result);
    return result;
  } catch (error) {
    console.error('  ❌ Error in getCustomerFromWindow:', error);
    return null;
  }
}

/**
 * Convert location data to checkout address format
 * @param {Object} locationData - Location data from API
 * @returns {Object} Address data formatted for checkout
 */
function convertLocationToAddress(locationData) {
  // Get customer data first to use company from there
  const customer = getCustomerFromWindow();
  const company = customer && customer.company ? customer.company : '';
  
  // Use full address as address1 (no company parsing)
  const address1 = locationData.address || '';

  // Hub postcode per delivery point. Casa stores a BLANK postalCode for every hub, so
  // without this the buyer is forced to type a postcode at checkout. These are the EXACT
  // codes Casa's Address Validation whitelists in "Allowed Postal Codes" per country
  // (Aug 2026) — the only values that pass checkout: Quito 170183, Miami 33172,
  // Amsterdam 1432 JH. (Casa's whitelist is exclusive, so e.g. 33122 is rejected even
  // though it's inside the allowed ZIP range.) Match on country first, then hub name.
  const HUB_POSTCODES = { 'ecuador': '170183', 'united states': '33172', 'netherlands': '1432 JH' };
  let zip = locationData.postalCode || '';
  if (!zip) {
    const country = String(locationData.country || '').toLowerCase();
    const name = String(locationData.name || '').toLowerCase();
    if (HUB_POSTCODES[country]) {
      zip = HUB_POSTCODES[country];
    } else if (name.indexOf('quito') !== -1) {
      zip = '170183';
    } else if (name.indexOf('miami') !== -1) {
      zip = '33172';
    } else if (name.indexOf('amsterdam') !== -1) {
      zip = '1432 JH';
    }
  }

  const result = {
    company: company, // Always from customer data
    address1: address1,
    address2: locationData.apartment || '',
    city: locationData.city || '',
    province: locationData.province || '',
    country: locationData.country || '',
    zip: zip
  };
  
  // Add customer name/phone from Liquid if available
  if (customer) {
    // Send both formats - let Shopify use whichever works for the country
    const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
    if (fullName) result.name = fullName;
    if (customer.firstName) result.firstName = customer.firstName;
    if (customer.lastName) result.lastName = customer.lastName;
    if (customer.phone) result.phone = customer.phone;
  }
  
  // Fallback to location phone if no customer phone
  if (!result.phone && locationData.phoneNo) {
    result.phone = locationData.phoneNo;
  }

  // NOTE: the postcode is filled from HUB_POSTCODES above (Marcel's confirmed hub codes) so
  // every field stays preloaded at checkout, matching how it worked before. If Shopify's
  // shipping/Markets config rejects one of these ("we don't ship to postal code ..."), that is
  // a shipping-settings issue to fix in admin for that destination, not a prefill problem.

  return result;
}

/**
 * Get address data from location data or return null
 * @returns {Object|null} Address data or null
 */
async function getAddressData() {
  // Try session storage first
  const sessionLocationData = getLocationData();
  if (sessionLocationData) {
    return convertLocationToAddress(sessionLocationData);
  }
  
  // Try cart attributes
  const cartLocationData = await getLocationDataFromCart();
  if (cartLocationData) {
    return convertLocationToAddress(cartLocationData);
  }
  
  return null;
}

/**
 * Build checkout URL with prefilled address
 * @param {Object} addressData - Address data to prefill
 * @returns {string} Checkout URL with address parameters
 */
async function buildCheckoutUrlWithAddress(addressData) {
  try {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    if (!cart.items || cart.items.length === 0) {
      console.error('❌ Cart is empty!');
      return null;
    }
    
    const cartItemsString = cart.items.map(item => `${item.variant_id}:${item.quantity}`).join(',');
    const queryParams = [];
    
    if (addressData.company) {
      queryParams.push(`checkout[shipping_address][company]=${encodeURIComponent(addressData.company)}`);
    }
    
    if (addressData.address1) {
      queryParams.push(`checkout[shipping_address][address1]=${encodeURIComponent(addressData.address1)}`);
    }
    
    if (addressData.address2) {
      queryParams.push(`checkout[shipping_address][address2]=${encodeURIComponent(addressData.address2)}`);
    }
    
    if (addressData.city) {
      queryParams.push(`checkout[shipping_address][city]=${encodeURIComponent(addressData.city)}`);
    }
    
    if (addressData.province) {
      queryParams.push(`checkout[shipping_address][province]=${encodeURIComponent(addressData.province)}`);
    }
    
    if (addressData.country) {
      queryParams.push(`checkout[shipping_address][country]=${encodeURIComponent(addressData.country)}`);
    }

    if (addressData.zip) {
      // trim() only — do NOT strip internal spaces. NL codes like "1432 JH" must keep the
      // space to match Casa's Address Validation whitelist exactly (encodeURIComponent -> %20).
      queryParams.push(
        `checkout[shipping_address][zip]=${encodeURIComponent(String(addressData.zip).trim())}`
      );
    }

    // Send both name formats - different countries use different fields
    if (addressData.name) {
      queryParams.push(`checkout[shipping_address][name]=${encodeURIComponent(addressData.name)}`);
    }
    
    if (addressData.firstName) {
      queryParams.push(`checkout[shipping_address][first_name]=${encodeURIComponent(addressData.firstName)}`);
    }
    
    if (addressData.lastName) {
      queryParams.push(`checkout[shipping_address][last_name]=${encodeURIComponent(addressData.lastName)}`);
    }
    
    if (addressData.phone) {
      queryParams.push(`checkout[shipping_address][phone]=${encodeURIComponent(addressData.phone)}`);
    }
    
    // Add cart attributes if they exist
    if (cart.attributes && typeof cart.attributes === 'object') {
      for (const [key, value] of Object.entries(cart.attributes)) {
        if (value && value !== '') {
          queryParams.push(`attributes[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`);
        }
      }
    }
    
    const baseUrl = window.location.origin;
    return `${baseUrl}/cart/${cartItemsString}?${queryParams.join('&')}`;
  } catch (error) {
    console.error('❌ Error building checkout URL:', error);
    return null;
  }
}

/**
 * Handle checkout button click
 */
async function handleCheckoutClick(e) {
  e.preventDefault();
  e.stopPropagation();
  
  if(false) console.log('🛒 Checkout button clicked, prefilling address...');
  
  // Clear the __ui localStorage key that causes checkout issues
  try {
    localStorage.removeItem('__ui');
    if(false) console.log('🗑️ Cleared __ui from localStorage');
  } catch (error) {
    console.warn('⚠️ Could not clear __ui from localStorage:', error);
  }
  
  const addressData = await getAddressData();
  
  if (!addressData) {
    if(false) console.log('ℹ️ No location data found, proceeding with normal checkout');
    // Allow normal checkout flow
    const href = e.target.href || e.target.closest('a')?.href;
    if (href) {
      window.location.href = href;
    }
    return;
  }
  
  if(false) console.log('📍 Using address data:', addressData);
  
  const checkoutUrl = await buildCheckoutUrlWithAddress(addressData);
  
  if (checkoutUrl) {
    if(false) console.log('✅ Redirecting to checkout with prefilled address:', checkoutUrl);
    window.location.href = checkoutUrl;
  } else {
    console.error('❌ Failed to build checkout URL, proceeding with normal checkout');
    const href = e.target.href || e.target.closest('a')?.href;
    if (href) {
      window.location.href = href;
    }
  }
}

/**
 * Bind to checkout buttons - ONLY intercepts manual clicks, never auto-clicks
 */
function bindCheckoutButtons() {
  // Only bind to actual checkout buttons/forms, not general cart links
  const selectors = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button[type="submit"][name="checkout"]',
    'form[action*="/checkout"] button[type="submit"]',
    'form[action*="/checkout"] input[type="submit"]',
    '[data-checkout-button]',
    '.checkout-button',
    'a.checkout-btn'
  ];
  
  let bound = false;
  
  selectors.forEach(selector => {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Skip if already bound
        if (element.dataset.addressPrefillBound) return;
        
        // Only bind to elements that are actually checkout buttons
        const isCheckoutButton = 
          element.name === 'checkout' ||
          element.getAttribute('data-checkout-button') !== null ||
          element.classList.contains('checkout-button') ||
          element.classList.contains('checkout-btn') ||
          (element.closest('form') && element.closest('form').action && element.closest('form').action.includes('/checkout'));
        
        if (!isCheckoutButton && selector.includes('form')) {
          // For form selectors, verify it's actually a checkout form
          const form = element.closest('form');
          if (!form || !form.action || !form.action.includes('/checkout')) {
            return;
          }
        }
        
        element.dataset.addressPrefillBound = 'true';
        
        if (element.tagName === 'FORM') {
          element.addEventListener('submit', handleCheckoutClick, { passive: false });
        } else {
          element.addEventListener('click', handleCheckoutClick, { passive: false, capture: false });
        }
        
        bound = true;
        if(false) console.log('✅ Bound to checkout element:', element.tagName, selector);
      });
    } catch (error) {
      console.warn('⚠️ Error binding to selector:', selector, error);
    }
  });
  
  if (bound) {
    if(false) console.log('✅ Checkout address prefill is active (manual clicks only)!');
  }
  
  return bound;
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bindCheckoutButtons();
  });
} else {
  bindCheckoutButtons();
}

// Also bind when new content is added (for dynamic pages) - but debounced to avoid excessive calls
let bindTimeout;
const observer = new MutationObserver(() => {
  clearTimeout(bindTimeout);
  bindTimeout = setTimeout(() => {
    bindCheckoutButtons();
  }, 300);
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

if(false) console.log('🚀 Address Prefill Script Loaded!');
if(false) console.log('📍 Location data will be auto-detected from:');
if(false) console.log('  1. Session Storage (casa_selected_location_data)');
if(false) console.log('  2. Cart Attributes (casa_selected_location_data)');
if(false) console.log('✅ Checkout buttons are now bound - click any checkout button to test!');
